import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Coffee, Coins, Loader2, LogOut } from "lucide-react";
import { Header } from "@/components/Header";
import { useCustomer } from "@/context/CustomerContext";
import { useCart } from "@/context/CartContext";
import { api, rupiah, STATUS, tableLabel, formatTime, COIN_VALUE } from "@/lib/api";

const CoinCard = ({ customer, history }) => (
  <div className="bg-forest text-paper rounded-2xl p-6 relative overflow-hidden grain" data-testid="member-coin-card">
    <div className="text-xs tracking-[0.15em] opacity-80">POIN MEMBER</div>
    <div className="flex items-end gap-2 mt-2"><Coins className="w-8 h-8 text-amber-300" /><span className="text-4xl font-bold" data-testid="member-coin-balance">{customer.coins}</span><span className="mb-1">Coin</span></div>
    <div className="text-sm opacity-80 mt-1">Senilai {rupiah(customer.coins * COIN_VALUE)} · tiap Rp1.000 = 1 Coin</div>
    {history.length > 0 && (
      <ul className="mt-4 space-y-1 text-xs opacity-90 max-h-36 overflow-y-auto" data-testid="member-coin-history">
        {history.map((h, i) => (
          <li key={i} className="flex justify-between gap-3"><span>{h.note} · {h.order_code}</span><span className={h.amount > 0 ? "text-amber-200" : ""}>{h.amount > 0 ? "+" : ""}{h.amount}</span></li>
        ))}
      </ul>
    )}
  </div>
);

export default function MyOrdersPage() {
  const { customer, login, logout } = useCustomer();
  const { myOrders } = useCart();
  const [orders, setOrders] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!customer) return;
    api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/customer/coins").then((r) => setHistory(r.data.history)).catch(() => {});
  }, [customer]);

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-10" data-testid="my-orders-page">
        <h1 className="font-serif font-semibold text-4xl">Pesanan Saya</h1>
        {customer === null && <Loader2 className="w-6 h-6 animate-spin text-forest mt-8" />}
        {customer === false && (
          <>
          {myOrders.length > 0 && (
            <div className="space-y-3 mt-6" data-testid="guest-orders">
              <p className="text-sm text-muted2">Pesanan dari perangkat ini:</p>
              {myOrders.map((o) => (
                <Link key={o.id} to={`/pesanan/${o.id}?t=${o.token}`} className="card-paper p-4 flex items-center justify-between gap-4 hover:border-forest transition-colors" data-testid={`guest-order-${o.id}`}>
                  <span className="font-semibold">{o.code}</span><ChevronRight className="w-5 h-5 text-muted2" />
                </Link>
              ))}
            </div>
          )}
          <div className="card-paper p-10 text-center mt-8" data-testid="my-orders-login">
            <p className="text-muted2">Mau jadi member & kumpulkan Coin? Masuk dengan Gmail untuk melihat semua pesanan dan Coin kamu.</p>
            <button onClick={login} className="btn-forest px-6 py-3 mt-5" data-testid="my-orders-login-button">Masuk dengan Gmail</button>
          </div>
          </>
        )}
        {customer && (
          <>
            <div className="flex items-center justify-between mt-2 text-sm text-muted2">
              <span data-testid="my-orders-customer">{customer.name} · {customer.email}</span>
              <button onClick={logout} className="flex items-center gap-1 hover:text-forest" data-testid="customer-logout-button"><LogOut className="w-4 h-4" /> Keluar</button>
            </div>
            <div className="mt-6"><CoinCard customer={customer} history={history} /></div>
            {orders && orders.length === 0 && (
              <div className="card-paper p-10 text-center mt-6" data-testid="my-orders-empty">
                <Coffee className="w-10 h-10 mx-auto text-sage-600" strokeWidth={1.4} />
                <p className="mt-3 text-muted2">Belum ada pesanan.</p>
                <Link to="/#menu" className="btn-forest inline-block px-6 py-3 mt-5">Lihat Menu</Link>
              </div>
            )}
            <div className="space-y-3 mt-6">
              {orders?.map((o) => (
                <Link key={o.id} to={`/pesanan/${o.id}?t=${o.access_token}`} className="card-paper p-5 flex items-center justify-between gap-4 hover:border-forest transition-colors" data-testid={`my-order-${o.id}`}>
                  <div>
                    <div className="font-semibold">{o.code} <span className="text-muted2 font-normal text-sm">· {tableLabel(o.floor, o.table)}</span></div>
                    <div className="text-xs text-muted2 mt-1">{formatTime(o.created_at)} · {rupiah(o.total)}{o.coins_awarded ? ` · +${o.coins_earned} Coin` : ""}</div>
                    <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${STATUS[o.status].cls}`}>{STATUS[o.status].label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted2" />
                </Link>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
